// ===========================================
// Xero Integration Page
// ===========================================
// Admin page for connecting and managing Xero accounting sync

import { usePermissions } from '../../hooks/usePermissions';
import { useOwnerTitle } from '../../hooks/useOwnerTitle';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  AccountBalance as AccountingIcon,
  Sync as SyncIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Refresh as RefreshIcon,
  Receipt as InvoiceIcon,
  People as ContactIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Replay as RetryIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import {
  useXeroStatus,
  useXeroAuthUrl,
  useDisconnectXero,
  useXeroAccounts,
  useXeroBankAccounts,
  useUpdateXeroSettings,
  useUpdateAccountMappings,
  useTriggerXeroSync,
  useXeroSyncStats,
  useXeroSyncErrors,
  useRetryXeroSync,
} from '../../hooks/useXero';
import { formatSyncTime, getSyncStatusColor, XeroAccount, XeroBankAccount } from '../../api/xero.api';

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function XeroIntegrationPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOwner, can } = usePermissions();
  const ownerTitle = useOwnerTitle();
  // Xero's settings, mappings and sync need integrations AND invoices write (roles plan 5.6).
  const canEditXero = can('integrations', 'write') && can('operations_invoices', 'write');
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [showAccountMappingDialog, setShowAccountMappingDialog] = useState(false);
  const [showSyncLogsExpanded, setShowSyncLogsExpanded] = useState(false);

  // Account mapping form state
  const [salesAccountCode, setSalesAccountCode] = useState<string>('');
  const [bankAccountId, setBankAccountId] = useState<string>('');

  // API hooks
  const { data: status, isLoading: statusLoading, refetch: refetchStatus } = useXeroStatus();
  const { data: accounts, isLoading: accountsLoading, refetch: fetchAccounts } = useXeroAccounts();
  const { data: bankAccounts, isLoading: bankAccountsLoading, refetch: fetchBankAccounts } = useXeroBankAccounts();
  const { data: syncStats, isLoading: syncStatsLoading } = useXeroSyncStats();
  const { data: syncErrors } = useXeroSyncErrors();

  const authUrlMutation = useXeroAuthUrl();
  const disconnectMutation = useDisconnectXero();
  const updateSettingsMutation = useUpdateXeroSettings();
  const updateAccountMappingsMutation = useUpdateAccountMappings();
  const syncMutation = useTriggerXeroSync();
  const retrySyncMutation = useRetryXeroSync();

  // The OAuth result, captured ONCE (#384): the effect below clears the URL after the first
  // render, and the banners used to read it live, so they vanished.
  const [oauthResult] = useState(() => ({
    connected: searchParams.get('connected') === 'true',
    error: searchParams.get('error'),
  }));

  // Handle OAuth callback results from URL params
  useEffect(() => {
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');

    if (connected === 'true') {
      refetchStatus();
      setSearchParams({});
    } else if (error) {
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, refetchStatus]);

  // Update form state when status loads
  useEffect(() => {
    if (status?.accountMappings) {
      setSalesAccountCode(status.accountMappings.salesAccountCode || '');
      setBankAccountId(status.accountMappings.bankAccountId || '');
    }
  }, [status]);

  // ===========================================
  // HANDLERS
  // ===========================================

  const handleConnect = async () => {
    const result = await authUrlMutation.mutateAsync();
    if (result.authUrl) {
      window.location.href = result.authUrl;
    }
  };

  const handleDisconnect = async () => {
    await disconnectMutation.mutateAsync();
    setShowDisconnectDialog(false);
  };

  const handleToggleSyncEnabled = async () => {
    await updateSettingsMutation.mutateAsync({
      syncEnabled: !status?.syncEnabled,
    });
  };

  const handleToggleSyncInvoices = async () => {
    await updateSettingsMutation.mutateAsync({
      syncInvoices: !status?.syncInvoices,
    });
  };

  const handleToggleSyncPayments = async () => {
    await updateSettingsMutation.mutateAsync({
      syncPayments: !status?.syncPayments,
    });
  };

  const handleToggleAutoCreateContacts = async () => {
    await updateSettingsMutation.mutateAsync({
      autoCreateContacts: !status?.autoCreateContacts,
    });
  };

  const handleOpenAccountMappings = async () => {
    await Promise.all([fetchAccounts(), fetchBankAccounts()]);
    setShowAccountMappingDialog(true);
  };

  const handleSaveAccountMappings = async () => {
    await updateAccountMappingsMutation.mutateAsync({
      salesAccountCode: salesAccountCode || null,
      bankAccountId: bankAccountId || null,
    });
    setShowAccountMappingDialog(false);
  };

  const handleManualSync = async (syncType: 'full' | 'invoices' | 'contacts' = 'full') => {
    await syncMutation.mutateAsync({ syncType });
  };

  const handleRetrySync = async (entityType: 'invoice' | 'contact', entityId: string) => {
    await retrySyncMutation.mutateAsync({ entityType, entityId });
  };

  // ===========================================
  // LOADING STATE
  // ===========================================

  if (statusLoading) {
    return (
      <Box>
        <PageHeader
          title="Xero Integration"
          subtitle="Connect your Xero account to sync invoices and payments"
        />
        <Card>
          <CardContent>
            <Skeleton variant="rectangular" height={200} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ===========================================
  // NOT CONNECTED STATE
  // ===========================================

  if (!status?.isConnected) {
    return (
      <Box>
        <PageHeader
          title="Xero Integration"
          subtitle="Connect your Xero account to sync invoices and payments"
        />

        {oauthResult.error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Connection Failed</AlertTitle>
            {oauthResult.error === 'auth_failed'
              ? 'Failed to authorize with Xero. Please try again.'
              : oauthResult.error === 'invalid_state'
                ? 'Invalid authorization state. Please try again.'
                : oauthResult.error === 'access_denied'
                  ? 'Access was denied. Please grant the required permissions.'
                  : oauthResult.error === 'not_permitted'
                    ? `Only your ${ownerTitle} can connect the school's Xero account.`
                    : 'An error occurred during authorization.'}
          </Alert>
        )}

        <Card>
          <CardHeader
            avatar={<AccountingIcon color="primary" />}
            title="Xero Accounting"
            subheader="Sync invoices and payments to your Xero account"
          />
          <CardContent>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Connect your Xero accounting software to automatically sync invoices and payments.
              This keeps your accounting records up to date without manual data entry.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                What you get:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Automatic invoice sync to Xero" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Payment recording in Xero when invoices are paid" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Automatic contact creation for families" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Account mapping for accurate bookkeeping" />
                </ListItem>
              </List>
            </Box>

            {/* Connecting the accounting link is the owner's act (roles plan §13 item 2). */}
            {isOwner ? (
              <Button
                variant="contained"
                startIcon={<LinkIcon />}
                onClick={handleConnect}
                disabled={authUrlMutation.isPending}
              >
                {authUrlMutation.isPending ? 'Connecting...' : 'Connect to Xero'}
              </Button>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>Only your {ownerTitle} can connect the school's Xero account.</Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ===========================================
  // CONNECTED STATE
  // ===========================================

  const hasInvoiceErrors = syncErrors?.invoices && syncErrors.invoices.length > 0;
  const hasContactErrors = syncErrors?.contacts && syncErrors.contacts.length > 0;

  return (
    <Box>
      <PageHeader
        title="Xero Integration"
        subtitle="Manage your Xero connection and sync settings"
      />

      {oauthResult.connected && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <AlertTitle>Connected Successfully</AlertTitle>
          Your Xero account is now connected. Configure your account mappings below to start syncing.
        </Alert>
      )}

      {/* Connection Status Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<AccountingIcon color="success" />}
          title="Xero Connected"
          subheader={status.tenantName ? `Organization: ${status.tenantName}` : 'Connected to Xero'}
          action={
            <Chip
              icon={<CheckIcon />}
              label="Connected"
              color="success"
              size="small"
            />
          }
        />
        <CardContent>
          <Stack spacing={2}>
            {/* Sync Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2">Automatic Sync</Typography>
                <Typography variant="body2" color="text.secondary">
                  Enable automatic syncing of invoices and payments
                </Typography>
              </Box>
              <Switch
                checked={status.syncEnabled}
                onChange={handleToggleSyncEnabled}
                disabled={updateSettingsMutation.isPending || !canEditXero}
              />
            </Box>

            <Divider />

            {/* Sync Invoices */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2">Sync Invoices</Typography>
                <Typography variant="body2" color="text.secondary">
                  Automatically sync invoices to Xero
                </Typography>
              </Box>
              <Switch
                checked={status.syncInvoices}
                onChange={handleToggleSyncInvoices}
                disabled={!status.syncEnabled || updateSettingsMutation.isPending || !canEditXero}
              />
            </Box>

            {/* Sync Payments */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2">Sync Payments</Typography>
                <Typography variant="body2" color="text.secondary">
                  Record payments in Xero when invoices are paid
                </Typography>
              </Box>
              <Switch
                checked={status.syncPayments}
                onChange={handleToggleSyncPayments}
                disabled={!status.syncEnabled || updateSettingsMutation.isPending || !canEditXero}
              />
            </Box>

            {/* Auto Create Contacts */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2">Auto-Create Contacts</Typography>
                <Typography variant="body2" color="text.secondary">
                  Automatically create contacts in Xero for families
                </Typography>
              </Box>
              <Switch
                checked={status.autoCreateContacts}
                onChange={handleToggleAutoCreateContacts}
                disabled={!status.syncEnabled || updateSettingsMutation.isPending || !canEditXero}
              />
            </Box>

            <Divider />

            {/* Account Mappings */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2">Account Mappings</Typography>
                <Typography variant="body2" color="text.secondary">
                  {status.accountMappings.salesAccountCode
                    ? `Sales: ${status.accountMappings.salesAccountCode}`
                    : 'Not configured'}
                  {status.accountMappings.bankAccountId && ' | Bank configured'}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SettingsIcon />}
                onClick={handleOpenAccountMappings}
                disabled={!canEditXero}
              >
                Configure
              </Button>
            </Box>

            <Divider />

            {/* Sync Stats */}
            {!syncStatsLoading && syncStats && (
              <>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Sync Status
                  </Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Chip
                      icon={<ContactIcon />}
                      label={`${syncStats.contacts.synced}/${syncStats.contacts.total} contacts`}
                      size="small"
                      color={syncStats.contacts.errors > 0 ? 'warning' : 'success'}
                      variant="outlined"
                    />
                    <Chip
                      icon={<InvoiceIcon />}
                      label={`${syncStats.invoices.synced}/${syncStats.invoices.total} invoices`}
                      size="small"
                      color={syncStats.invoices.errors > 0 ? 'warning' : 'success'}
                      variant="outlined"
                    />
                    {syncStats.invoices.pending > 0 && (
                      <Chip
                        icon={<SyncIcon />}
                        label={`${syncStats.invoices.pending} pending`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {(syncStats.contacts.errors > 0 || syncStats.invoices.errors > 0) && (
                      <Chip
                        icon={<ErrorIcon />}
                        label={`${syncStats.contacts.errors + syncStats.invoices.errors} errors`}
                        size="small"
                        color="error"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Box>
                <Divider />
              </>
            )}

            {/* Last Sync & Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2">Last Sync</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatSyncTime(status.lastSyncAt)}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={syncMutation.isPending ? <CircularProgress size={16} /> : <SyncIcon />}
                  onClick={() => handleManualSync('full')}
                  disabled={syncMutation.isPending || !canEditXero}
                >
                  Sync Now
                </Button>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => handleManualSync('invoices')}
                  disabled={syncMutation.isPending || !canEditXero}
                >
                  Invoices Only
                </Button>
              </Stack>
            </Box>

            <Divider />

            {/* Disconnect: owner only (roles plan §13 item 2) */}
            {isOwner && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="text"
                  color="error"
                  startIcon={<LinkOffIcon />}
                  onClick={() => setShowDisconnectDialog(true)}
                >
                  Disconnect
                </Button>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Sync Errors Card */}
      {(hasInvoiceErrors || hasContactErrors) && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            avatar={<WarningIcon color="warning" />}
            title="Sync Errors"
            subheader={`${(syncErrors?.invoices?.length || 0) + (syncErrors?.contacts?.length || 0)} item(s) failed to sync`}
          />
          <CardContent>
            {hasInvoiceErrors && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Invoice Errors
                </Typography>
                <List dense>
                  {syncErrors.invoices.map((error) => (
                    <ListItem
                      key={error.invoiceId}
                      secondaryAction={
                        <Tooltip title="Retry sync">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleRetrySync('invoice', error.invoiceId)}
                            disabled={retrySyncMutation.isPending || !canEditXero}
                          >
                            <RetryIcon />
                          </IconButton>
                        </Tooltip>
                      }
                    >
                      <ListItemIcon>
                        <ErrorIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`Invoice ${error.invoiceNumber}`}
                        secondary={error.error}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
            {hasContactErrors && (
              <>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: hasInvoiceErrors ? 2 : 0 }}>
                  Contact Errors
                </Typography>
                <List dense>
                  {syncErrors.contacts.map((error) => (
                    <ListItem
                      key={error.familyId}
                      secondaryAction={
                        <Tooltip title="Retry sync">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleRetrySync('contact', error.familyId)}
                            disabled={retrySyncMutation.isPending || !canEditXero}
                          >
                            <RetryIcon />
                          </IconButton>
                        </Tooltip>
                      }
                    >
                      <ListItemIcon>
                        <ErrorIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={error.familyName}
                        secondary={error.error}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent Sync Logs */}
      {!syncStatsLoading && syncStats?.recentLogs && syncStats.recentLogs.length > 0 && (
        <Card>
          <CardHeader
            title="Recent Sync Activity"
            action={
              <IconButton onClick={() => setShowSyncLogsExpanded(!showSyncLogsExpanded)}>
                {showSyncLogsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            }
          />
          <Collapse in={showSyncLogsExpanded}>
            <CardContent>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Entity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {syncStats.recentLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{log.syncType.replace(/_/g, ' ')}</TableCell>
                        <TableCell>{log.entityType}</TableCell>
                        <TableCell>
                          <Chip
                            label={log.status}
                            size="small"
                            color={getSyncStatusColor(log.status)}
                          />
                        </TableCell>
                        <TableCell>{formatSyncTime(log.startedAt)}</TableCell>
                        <TableCell>
                          {log.errorMessage && (
                            <Typography variant="body2" color="error" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {log.errorMessage}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Collapse>
        </Card>
      )}

      {/* Account Mapping Dialog */}
      <Dialog
        open={showAccountMappingDialog}
        onClose={() => setShowAccountMappingDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Configure Account Mappings</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Map your Music &apos;n Me accounts to Xero accounts for accurate bookkeeping.
          </Typography>

          {(accountsLoading || bankAccountsLoading) ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel>Sales Account</InputLabel>
                <Select
                  value={salesAccountCode}
                  onChange={(e) => setSalesAccountCode(e.target.value)}
                  label="Sales Account"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {accounts?.filter((a: XeroAccount) => a.type === 'REVENUE' && a.status === 'ACTIVE').map((account: XeroAccount) => (
                    <MenuItem key={account.accountId} value={account.code}>
                      {account.code} - {account.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Bank Account (for payments)</InputLabel>
                <Select
                  value={bankAccountId}
                  onChange={(e) => setBankAccountId(e.target.value)}
                  label="Bank Account (for payments)"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {bankAccounts?.map((account: XeroBankAccount) => (
                    <MenuItem key={account.accountId} value={account.accountId}>
                      {account.code} - {account.name} ({account.currencyCode})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAccountMappingDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveAccountMappings}
            disabled={updateAccountMappingsMutation.isPending}
          >
            {updateAccountMappingsMutation.isPending ? 'Saving...' : 'Save Mappings'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={showDisconnectDialog}
        onClose={() => setShowDisconnectDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Disconnect Xero?</DialogTitle>
        <DialogContent>
          <Typography>
            This will remove the connection to your Xero account. Synced invoices and contacts
            will remain in Xero but will no longer be updated automatically.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisconnectDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDisconnect}
            disabled={disconnectMutation.isPending}
          >
            {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
